/**
 * Pantone (PMS) to approximate sRGB hex, used to draw a color chip beside the Pantone code
 * SanMar returns as `approximatePms`.
 *
 * Keys are the Pantone designation lower-cased with every non-alphanumeric character removed,
 * so "COOL GRAY 11C" is keyed "coolgray11c" and "426C" is keyed "426c".
 *
 * Merged from two public conversion tables plus a small alias map for the named process inks
 * (PROCESS BLACK C and friends) that numeric tables omit. It covers 86% of the color rows
 * currently staged. The gap is Pantone's post-2016 2xxx-4xxx range, which no freely available
 * table carries; those codes render no chip rather than a misleading one.
 *
 * These are approximations for on-screen disambiguation, not color-accurate reproduction —
 * which is also how SanMar labels the field. PANTONE is a trademark of Pantone LLC.
 */
export const PANTONE_HEX: Record<string, string> = {
  "100":"#ffff7d",
  "100c":"#f6eb61",
  "101":"#ffff36",
  "101c":"#f7ea48",
  "102":"#fffc0d",
  "102c":"#fce300",
  "103":"#d1cb00",
  "103c":"#c5a900",
  "104":"#b3ad00",
  "104c":"#af9800",
  "105":"#807c00",
  "105c":"#897a27",
  "106":"#fffa4f",
  "106c":"#f9e547",
  "107":"#fff536",
  "107c":"#fbe122",
  "108":"#fff00d",
  "108c":"#fedb00",
  "109":"#ffe600",
  "109c":"#ffd100",
  "110":"#edd100",
  "110c":"#daaa00",
  "111":"#baa600",
  "111c":"#aa8a00",
  "112":"#9e8e00",
  "112c":"#9c8412",
  "113":"#ffed57",
  "113c":"#fae053",
  "114":"#ffeb45",
  "114c":"#fbdd40",
  "115":"#ffe833",
  "115c":"#fdda24",
  "116":"#ffd600",
  "1162x":"#ff9105",
  "1162xc":"#ff9105",
  "116c":"#ffcd00",
  "117":"#d9b200",
  "117c":"#c99700",
  "118":"#ba9900",
  "118c":"#ac8400",
  "119":"#827200",
  "119c":"#897322",
  "120":"#ffe86b",
  "1205":"#fff2b0",
  "1205c":"#f8e08e",
  "120c":"#fbdb65",
  "121":"#ffe34f",
  "1215":"#ffe88c",
  "1215c":"#fbd872",
  "121c":"#fdd757",
  "122":"#ffd433",
  "1225":"#ffd461",
  "1225c":"#ffc845",
  "122c":"#fed141",
  "123":"#ffc20f",
  "1235":"#ffb517",
  "1235c":"#ffb81c",
  "123c":"#ffc72c",
  "124":"#f0ad00",
  "1245":"#d19700",
  "1245c":"#c69214",
  "124c":"#eaaa00",
  "125":"#bd8c00",
  "1255":"#a87b00",
  "1255c":"#ad841f",
  "125c":"#b58500",
  "126":"#a17800",
  "1265":"#7d5b00",
  "1265c":"#886b25",
  "126c":"#9a7611",
  "127":"#ffed80",
  "127c":"#f3dd6d",
  "128":"#ffe359",
  "128c":"#f3d54e",
  "129":"#ffd63b",
  "129c":"#f3d03e",
  "130":"#ffb300",
  "1302x":"#ff6b08",
  "1302xc":"#ff6b08",
  "130c":"#f2a900",
  "131":"#e89e00",
  "131c":"#cc8a00",
  "132":"#b38100",
  "132c":"#a07400",
  "133":"#705a00",
  "133c":"#6c571b",
  "134":"#ffe38c",
  "1345":"#ffdb87",
  "1345c":"#fdd086",
  "134c":"#fdd26e",
  "135":"#ffcf66",
  "1355":"#ffcc70",
  "1355c":"#ffc56e",
  "135c":"#ffc658",
  "136":"#ffba3d",
  "1365":"#ffb547",
  "1365c":"#ffb549",
  "136c":"#ffbf3f",
  "137":"#ffa61a",
  "1375":"#ff991a",
  "1375c":"#ff9e1b",
  "137c":"#ffa300",
  "138":"#fc9200",
  "1385":"#ed8500",
  "1385c":"#d57800",
  "138c":"#de7c00",
  "139":"#c47c00",
  "1395":"#a15f00",
  "1395c":"#996017",
  "139c":"#af6d04",
  "140":"#755600",
  "1405":"#5e3c00",
  "1405c":"#6e4c1e",
  "140c":"#74531c",
  "141":"#ffcf7d",
  "141c":"#f2c75c",
  "142":"#ffb83d",
  "142c":"#f1be48",
  "143":"#ffa626",
  "143c":"#f1b434",
  "144":"#ff8500",
  "144c":"#ed8b00",
  "145":"#eb7c00",
  "145c":"#cf7f00",
  "146":"#ab6100",
  "146c":"#a76d11",
  "147":"#705100",
  "147c":"#715c2a",
  "148":"#ffd6a1",
  "1485":"#ffba75",
  "1485c":"#ffae62",
  "148c":"#fecb8b",
  "149":"#ffc487",
  "1495":"#ffab54",
  "1495c":"#ff8f1c",
  "149c":"#ffc27b",
  "150":"#ffa64d",
  "1505":"#ff943b",
  "1505c":"#ff6900",
  "150c":"#ffb25b",
  "151":"#ff850d",
  "151c":"#ff8200",
  "152":"#fc7c00",
  "1525":"#e66000",
  "1525c":"#b94700",
  "152c":"#e57200",
  "153":"#d17100",
  "1535":"#9e4a00",
  "1535c":"#94450b",
  "153c":"#be6a14",
  "154":"#a85b00",
  "1545":"#472200",
  "1545c":"#653819",
  "154c":"#9b5a1a",
  "155":"#ffe0b8",
  "1555":"#ffc7a8",
  "1555c":"#ffb990",
  "155c":"#efd19f",
  "156":"#ffc794",
  "1565":"#ffa882",
  "1565c":"#ffa06a",
  "156c":"#efbe7d",
  "157":"#ff914d",
  "1575":"#ff8c47",
  "1575c":"#ff7f32",
  "157c":"#eca154",
  "158":"#ff6308",
  "1585":"#ff701a",
  "1585c":"#ff6a13",
  "158c":"#e87722",
  "159":"#ed5100",
  "1595":"#f26300",
  "1595c":"#d86018",
  "159c":"#cb6015",
  "160":"#ad4200",
  "1605":"#b34f00",
  "1605c":"#a65523",
  "160c":"#a1561c",
  "161":"#5c2c00",
  "1615":"#914000",
  "1615c":"#8b4720",
  "161c":"#603d20",
  "162":"#ffd9c7",
  "1625":"#ffb0a1",
  "1625c":"#ffa38b",
  "162c":"#ffbe9f",
  "163":"#ffb08f",
  "1635":"#ff9c85",
  "1635c":"#ff8d6d",
  "163c":"#ff9d6e",
  "164":"#ff8a45",
  "1645":"#ff8257",
  "1645c":"#ff6a39",
  "164c":"#ff7f41",
  "165":"#ff690a",
  "1652x":"#ff080a",
  "1652xc":"#ff080a",
  "1655":"#ff5e17",
  "1655c":"#fc4c02",
  "165c":"#ff671f",
  "166":"#ff5c00",
  "1665":"#ff5200",
  "1665c":"#dc4405",
  "166c":"#e35205",
  "167":"#d45500",
  "1675":"#b83d00",
  "1675c":"#a9431e",
  "167c":"#be531c",
  "168":"#692d00",
  "1685":"#8f2e00",
  "1685c":"#833921",
  "168c":"#73381d",
  "169":"#ffcccc",
  "169c":"#ffb3ab",
  "170":"#ff998f",
  "170c":"#ff8674",
  "171":"#ff7852",
  "171c":"#ff5c39",
  "172":"#ff571f",
  "172c":"#fa4616",
  "173":"#f54c00",
  "173c":"#cf4520",
  "174":"#a33100",
  "174c":"#963821",
  "175":"#662400",
  "175c":"#6b3529",
  "176":"#ffbfd1",
  "1765":"#ff9ec9",
  "1765c":"#ffa3b5",
  "1767":"#ffbae0",
  "1767c":"#fcafc0",
  "176c":"#ffb1bb",
  "177":"#ff8c99",
  "1775":"#ff87b5",
  "1775c":"#ff8da1",
  "1777":"#ff6ba3",
  "1777c":"#fb637e",
  "177c":"#ff808b",
  "178":"#ff6970",
  "1785":"#ff5480",
  "1785c":"#f8485e",
  "1787":"#ff3d66",
  "1787c":"#f4364c",
  "1788":"#ff291f",
  "17882x":"#fc000a",
  "17882xc":"#fc000a",
  "1788c":"#ee2737",
  "178c":"#ff585d",
  "179":"#ff3600",
  "1795":"#ff0f00",
  "1795c":"#d22630",
  "1797":"#f50002",
  "1797c":"#cb333b",
  "179c":"#e03c31",
  "180":"#e33000",
  "1805":"#c41200",
  "1805c":"#af272f",
  "1807":"#b80007",
  "1807c":"#a4343a",
  "180c":"#be3a34",
  "181":"#872300",
  "1815":"#7d0c00",
  "1815c":"#7c2529",
  "1817":"#570900",
  "1817c":"#643335",
  "181c":"#81312f",
  "182":"#ffbde6",
  "182c":"#fabbcb",
  "183":"#ff8ac9",
  "183c":"#fc9bb3",
  "184":"#ff5296",
  "184c":"#f65275",
  "185":"#ff173d",
  "1852x":"#fc000a",
  "1852xc":"#fc000a",
  "185c":"#e4002b",
  "186":"#f5002f",
  "186c":"#c8102e",
  "187":"#cc002b",
  "187c":"#a6192e",
  "188":"#800400",
  "188c":"#76232f",
  "189":"#ffa1e6",
  "1895":"#ffb8ed",
  "1895c":"#f5b6cd",
  "189c":"#f8a3bc",
  "190":"#ff73c7",
  "1905":"#ff96e8",
  "1905c":"#f59bbb",
  "190c":"#f67599",
  "191":"#ff3d9e",
  "1915":"#ff4acc",
  "1915c":"#ef4a81",
  "191c":"#ef426f",
  "192":"#ff0052",
  "1925":"#ff0073",
  "1925c":"#e0004d",
  "192c":"#e40046",
  "193":"#de004b",
  "1935":"#f20068",
  "1935c":"#c5003e",
  "193c":"#bf0d3e",
  "194":"#ab003e",
  "1945":"#cf005b",
  "1945c":"#a6093d",
  "194c":"#9b2743",
  "195":"#73002e",
  "1955":"#a10040",
  "1955c":"#8a1538",
  "195c":"#782f40",
  "196":"#ffbff5",
  "196c":"#ecc7cd",
  "197":"#ff8ce6",
  "197c":"#e89cae",
  "198":"#ff38ab",
  "198c":"#df4661",
  "199":"#ff0061",
  "199c":"#d50032",
  "200":"#e00053",
  "200c":"#ba0c2f",
  "201":"#b50043",
  "201c":"#9d2235",
  "202":"#910039",
  "202c":"#862633",
  "203":"#ffa8f7",
  "203c":"#ecb3cb",
  "204":"#ff6bf7",
  "204c":"#e782a9",
  "205":"#ff29e8",
  "205c":"#e0457b",
  "206":"#f70099",
  "206c":"#ce0037",
  "207":"#cf0076",
  "207c":"#a50034",
  "208":"#a10067",
  "208c":"#861f41",
  "209":"#78004f",
  "209c":"#6f263d",
  "210":"#ff9cf0",
  "210c":"#f99fc9",
  "211":"#ff73eb",
  "211c":"#f57eb6",
  "212":"#ff47e3",
  "212c":"#f04e98",
  "213":"#ff0dba",
  "213c":"#e31c79",
  "214":"#eb009b",
  "214c":"#ce0f69",
  "215":"#ba0079",
  "215c":"#ac145a",
  "216":"#82074e",
  "216c":"#7d2248",
  "217":"#ffb8ff",
  "217c":"#eabedb",
  "218":"#fa63ff",
  "218c":"#e56db1",
  "219":"#fc1fff",
  "219c":"#da1884",
  "220":"#d400b8",
  "220c":"#a50050",
  "221":"#b30098",
  "221c":"#910048",
  "222":"#69005e",
  "222c":"#6c1d45",
  "223":"#ff8aff",
  "223c":"#ef95cf",
  "224":"#fc5eff",
  "224c":"#eb6fbd",
  "225":"#fc2bff",
  "225c":"#df1995",
  "226":"#ff00ff",
  "226c":"#d0006f",
  "227":"#cf00c0",
  "227c":"#aa0061",
  "228":"#960090",
  "228c":"#890c58",
  "229":"#660057",
  "229c":"#672146",
  "230":"#ffa8ff",
  "230c":"#f4a6d7",
  "231":"#fc7aff",
  "231c":"#f277c6",
  "232":"#f754ff",
  "232c":"#e93cac",
  "233":"#e300ff",
  "233c":"#c6007e",
  "234":"#b100bd",
  "234c":"#a20067",
  "235":"#910099",
  "235c":"#840b55",
  "236":"#fcb3ff",
  "2365":"#fabaff",
  "2365c":"#efbae1",
  "236c":"#f1a7dc",
  "237":"#f782ff",
  "2375":"#e66eff",
  "2375c":"#e277cd",
  "237c":"#ec86d0",
  "238":"#f05eff",
  "2385":"#cf36ff",
  "2385c":"#d539b5",
  "238c":"#e45dbf",
  "239":"#e336ff",
  "2392x":"#e800a1",
  "2392xc":"#e800a1",
  "2395":"#ba0dff",
  "2395c":"#c800a1",
  "239c":"#db3eb1",
  "240":"#d10fff",
  "2405":"#a800ff",
  "2405c":"#b0008e",
  "240c":"#c5299b",
  "241":"#b600fa",
  "2415":"#9d00eb",
  "2415c":"#9e007e",
  "241c":"#af1685",
  "242":"#750082",
  "2425":"#7700bd",
  "2425c":"#830065",
  "242c":"#80225f",
  "243":"#f2b5ff",
  "243c":"#eab8e4",
  "244":"#e89eff",
  "244c":"#e59bdc",
  "245":"#db78ff",
  "245c":"#dd7fd3",
  "246":"#b51aff",
  "246c":"#c724b1",
  "247":"#a300ff",
  "247c":"#bb16a3",
  "248":"#9600fa",
  "248c":"#a51890",
  "249":"#6e00b8",
  "249c":"#80276c",
  "250":"#f2d1ff",
  "250c":"#e7bae4",
  "251":"#de9cff",
  "251c":"#dd9cdf",
  "252":"#c270ff",
  "252c":"#c964cf",
  "253":"#910dff",
  "253c":"#ad1aac",
  "254":"#8000ff",
  "254c":"#981d97",
  "255":"#5e00bf",
  "255c":"#72246c",
  "256":"#edccff",
  "2562":"#cfa6ff",
  "2562c":"#d7a9e3",
  "2563":"#c7abff",
  "2563c":"#cba3d8",
  "2567":"#b5a3ff",
  "2567c":"#c1a0da",
  "256c":"#d6bfdd",
  "257":"#dba8ff",
  "2572":"#b387ff",
  "2572c":"#c98bdb",
  "2573":"#b391ff",
  "2573c":"#b884cb",
  "2577":"#998cff",
  "2577c":"#a77bca",
  "257c":"#c6a1cf",
  "258":"#913dff",
  "2582":"#8a47ff",
  "2582c":"#ac4fc6",
  "2583":"#8a5eff",
  "2583c":"#a05eb5",
  "2587":"#6957ff",
  "2587c":"#8246af",
  "258c":"#8c4799",
  "259":"#5f00d9",
  "2592":"#661aff",
  "25922x":"#3005ff",
  "25922xc":"#3005ff",
  "2592c":"#9b26b6",
  "2593":"#631cff",
  "2593c":"#84329b",
  "2597":"#2600ff",
  "2597c":"#5c068c",
  "259c":"#6d2077",
  "260":"#5b00bd",
  "2602":"#5c00f7",
  "2602c":"#87189d",
  "2603":"#4d00fa",
  "2603c":"#702082",
  "2607":"#2d00ed",
  "2607c":"#500778",
  "260c":"#642667",
  "261":"#500099",
  "2612":"#4f00db",
  "2612c":"#772583",
  "2613":"#5000d9",
  "2613c":"#671e75",
  "2617":"#2e00d9",
  "2617c":"#470a68",
  "261c":"#5d285f",
  "262":"#3f0073",
  "2622":"#3c008f",
  "2622c":"#653165",
  "2623":"#4700ad",
  "2623c":"#5f2167",
  "2627":"#2800b0",
  "2627c":"#3c1053",
  "262c":"#51284f",
  "263":"#e6dbff",
  "2635":"#b8baff",
  "2635c":"#c5b4e3",
  "263c":"#d7c6e6",
  "264":"#bdb8ff",
  "2645":"#99a3ff",
  "2645c":"#ad96dc",
  "264c":"#c1a7e2",
  "265":"#7570ff",
  "2655":"#7582ff",
  "2655c":"#9678d3",
  "265c":"#9063cd",
  "266":"#361aff",
  "2665":"#6166ff",
  "2665c":"#7d55c7",
  "266c":"#753bbd",
  "267":"#1c00ff",
  "267c":"#5f259f",
  "268":"#2800e0",
  "2685":"#0900e6",
  "2685c":"#330072",
  "268c":"#582c83",
  "269":"#2600ab",
  "2695":"#0c0082",
  "2695c":"#2e1a47",
  "269c":"#512d6d",
  "270":"#b0baff",
  "2705":"#99b3ff",
  "2705c":"#a7a4e0",
  "2706":"#cfe8ff",
  "2706c":"#cbd3eb",
  "2707":"#d4f0ff",
  "2707c":"#c3d7ee",
  "2708":"#bde6ff",
  "2708c":"#b8ccea",
  "270c":"#b4b5df",
  "271":"#91a1ff",
  "2715":"#6e8cff",
  "2715c":"#8b84d7",
  "2716":"#8cb5ff",
  "2716c":"#9faee5",
  "2717":"#b5e0ff",
  "2717c":"#a7c6ed",
  "2718":"#5496ff",
  "2718c":"#5c88da",
  "271c":"#9595d2",
  "272":"#6b85ff",
  "2725":"#3b52ff",
  "2725c":"#685bc7",
  "2726":"#3657ff",
  "2726c":"#485cc7",
  "2727":"#4a94ff",
  "2727c":"#307fe2",
  "2728":"#0a4fff",
  "2728c":"#0047bb",
  "272c":"#7474c1",
  "273":"#0009eb",
  "2735":"#000dff",
  "2735c":"#2e008b",
  "2736":"#0017ff",
  "2736c":"#1e22aa",
  "2738":"#0020fa",
  "2738c":"#06038d",
  "273c":"#24135f",
  "274":"#0000b8",
  "2745":"#000bd9",
  "2745c":"#280071",
  "2746":"#0012e6",
  "2746c":"#171c8f",
  "2747":"#001ed9",
  "2747c":"#001a72",
  "2748":"#001ad9",
  "2748c":"#001871",
  "274c":"#211551",
  "275":"#030091",
  "2755":"#0005b3",
  "2755c":"#250e62",
  "2756":"#000bb5",
  "2756c":"#151f6d",
  "2757":"#0020b3",
  "2757c":"#001e60",
  "2758":"#0026bd",
  "2758c":"#001e62",
  "275c":"#201747",
  "276":"#020073",
  "2765":"#00048c",
  "2765c":"#201547",
  "2766":"#000887",
  "2766c":"#141b4d",
  "2767":"#001a75",
  "2767c":"#13294b",
  "2768":"#001f8f",
  "2768c":"#071d49",
  "276c":"#221c35",
  "277":"#baedff",
  "277c":"#abcae9",
  "278":"#9cdbff",
  "278c":"#8bb8e8",
  "279":"#52a8ff",
  "279c":"#418fde",
  "280":"#003bd1",
  "280c":"#012169",
  "281":"#0031ad",
  "281c":"#00205b",
  "282":"#002675",
  "282c":"#041e42",
  "283":"#a6e8ff",
  "283c":"#92c1e9",
  "284":"#73cfff",
  "284c":"#6cace4",
  "285":"#1c91ff",
  "285c":"#0072ce",
  "286":"#0055fa",
  "286c":"#0033a0",
  "287":"#0048e0",
  "287c":"#003087",
  "288":"#0041c4",
  "288c":"#002d72",
  "289":"#00246b",
  "289c":"#0c2340",
  "290":"#bffaff",
  "2905":"#96faff",
  "2905c":"#8dc8e8",
  "290c":"#b9d9eb",
  "291":"#abf7ff",
  "2915":"#69edff",
  "2915c":"#62b5e5",
  "291c":"#9bcbeb",
  "292":"#82e3ff",
  "2925":"#26c2ff",
  "2925c":"#009cde",
  "292c":"#69b3e7",
  "293":"#006bfa",
  "2935":"#008aff",
  "2935c":"#0057b8",
  "293c":"#003da5",
  "294":"#0055c9",
  "2945":"#0079db",
  "2945c":"#004c97",
  "294c":"#002f6c",
  "295":"#0045a1",
  "2955":"#0058a1",
  "2955c":"#003865",
  "295c":"#002855",
  "296":"#00294d",
  "2965":"#00395c",
  "2965c":"#00263e",
  "296c":"#041c2c",
  "297":"#82fcff",
  "2975":"#b3fff2",
  "2975c":"#99d6ea",
  "297c":"#71c5e8",
  "298":"#4fedff",
  "2985":"#69fff0",
  "2985c":"#5bc2e7",
  "298c":"#41b6e6",
  "299":"#26cfff",
  "2992x":"#03bfff",
  "2992xc":"#03bfff",
  "2995":"#1ae3ff",
  "2995c":"#00a9e0",
  "299c":"#00a3e0",
  "300":"#008fff",
  "3005":"#00a0fa",
  "3005c":"#0077c8",
  "300c":"#005eb8",
  "301":"#0073d1",
  "3015":"#0089cc",
  "3015c":"#00629b",
  "301c":"#004b87",
  "302":"#006080",
  "3025":"#00687d",
  "3025c":"#004f71",
  "302c":"#003b5c",
  "303":"#003b42",
  "3035":"#004744",
  "3035c":"#003e51",
  "303c":"#002a3a",
  "304":"#b3ffeb",
  "304c":"#9adbe8",
  "305":"#7dffe8",
  "305c":"#59cbe8",
  "306":"#40ffed",
  "3062x":"#0dfcf2",
  "3062xc":"#0dfcf2",
  "306c":"#00b5e2",
  "307":"#009cba",
  "307c":"#006ba6",
  "308":"#008087",
  "308c":"#00587c",
  "309":"#004741",
  "309c":"#003b49",
  "310":"#91ffe6",
  "3105":"#91ffe0",
  "3105c":"#68d2df",
  "310c":"#6ad1e3",
  "311":"#5effe0",
  "3115":"#5effd1",
  "3115c":"#00c1d5",
  "311c":"#05c3de",
  "312":"#0affe3",
  "3125":"#2bffc9",
  "3125c":"#00aec7",
  "312c":"#00a9ce",
  "313":"#00decc",
  "3135":"#00e8c3",
  "3135c":"#008eaa",
  "313c":"#0092bc",
  "314":"#00b3a2",
  "3145":"#00c49f",
  "3145c":"#00778b",
  "314c":"#007fa3",
  "315":"#009180",
  "3155":"#009e78",
  "3155c":"#006272",
  "315c":"#00677f",
  "316":"#00523c",
  "3165":"#005940",
  "3165c":"#004f59",
  "316c":"#004851",
  "317":"#d1ffeb",
  "317c":"#b1e4e3",
  "318":"#9effd9",
  "318c":"#88dbdf",
  "319":"#7affcf",
  "319c":"#2dccd3",
  "320":"#00eda4",
  "3202x":"#00ccbf",
  "3202xc":"#00ccbf",
  "320c":"#009ca6",
  "321":"#00c487",
  "321c":"#008c95",
  "322":"#00a66f",
  "322c":"#007377",
  "323":"#008754",
  "323c":"#005f61",
  "324":"#b8ffe0",
  "3242":"#a1ffd1",
  "3242c":"#71dbd4",
  "3245":"#a8ffcf",
  "3245c":"#7ce0d3",
  "3248":"#91ffc2",
  "3248c":"#6dcdb8",
  "324c":"#9cdbd9",
  "325":"#70ffbd",
  "3252":"#87ffc2",
  "3252c":"#2ad2c9",
  "3255":"#82ffb8",
  "3255c":"#2cd5c4",
  "3258":"#69ffab",
  "3258c":"#49c5b1",
  "325c":"#64ccc9",
  "326":"#21ff9e",
  "3262":"#4affab",
  "3262c":"#00bfb3",
  "3265":"#4fffa1",
  "3265c":"#00c7b1",
  "3268":"#1aff82",
  "3268c":"#00ab8e",
  "326c":"#00b2a9",
  "327":"#00d477",
  "3272":"#00ff8f",
  "3272c":"#00a499",
  "3272x":"#02a80a",
  "3272xc":"#02a80a",
  "3275":"#0dff87",
  "3275c":"#00b398",
  "3278":"#00f26d",
  "3278c":"#009b77",
  "327c":"#008675",
  "328":"#00ad5f",
  "3282":"#00d975",
  "3282c":"#008578",
  "3285":"#00ed77",
  "3285c":"#009681",
  "3288":"#00cc5e",
  "3288c":"#008264",
  "328c":"#007367",
  "329":"#008a4a",
  "3292":"#008a46",
  "3292c":"#00594f",
  "3295":"#00c95f",
  "3295c":"#007864",
  "3298":"#009440",
  "3298c":"#006a52",
  "329c":"#00685e",
  "330":"#006635",
  "3302":"#004f24",
  "3302c":"#004c45",
  "3305":"#006327",
  "3305c":"#004e42",
  "3308":"#00471d",
  "3308c":"#034638",
  "330c":"#00534c",
  "331":"#c2ffd6",
  "331c":"#a7e6d7",
  "332":"#b3ffcc",
  "332c":"#8ce2d0",
  "333":"#91ffba",
  "333c":"#3cdbc0",
  "334":"#00f763",
  "334c":"#009775",
  "335":"#00b33e",
  "335c":"#007b5f",
  "336":"#00872d",
  "336c":"#00664f",
  "337":"#b0ffcc",
  "3375":"#a6ffbf",
  "3375c":"#7ae1bf",
  "337c":"#8fd6bd",
  "338":"#87ffad",
  "3385":"#8cffab",
  "3385c":"#47d7ac",
  "338c":"#6eceb2",
  "339":"#29ff70",
  "3395":"#63ff8c",
  "3395c":"#00c389",
  "339c":"#00b388",
  "340":"#00e84f",
  "3405":"#26ff59",
  "3405c":"#00af66",
  "340c":"#00965e",
  "341":"#00b53c",
  "3415":"#00c72e",
  "3415c":"#007749",
  "341c":"#007a53",
  "342":"#00912a",
  "3425":"#009421",
  "3425c":"#006341",
  "342c":"#006747",
  "343":"#02631c",
  "3435":"#005710",
  "3435c":"#154734",
  "343c":"#115740",
  "344":"#baffc4",
  "344c":"#a0dab3",
  "345":"#9effad",
  "345c":"#91d6ac",
  "346":"#73ff87",
  "346c":"#71cc98",
  "347":"#00f723",
  "347c":"#009a44",
  "348":"#00c21d",
  "348c":"#00843d",
  "349":"#00940d",
  "349c":"#046a38",
  "350":"#0d4000",
  "350c":"#2c5234",
  "351":"#d4ffd6",
  "351c":"#a2e4b8",
  "352":"#baffbf",
  "352c":"#8fe2b0",
  "353":"#9effa3",
  "353c":"#80e0a7",
  "354":"#33ff1a",
  "3542x":"#05ff05",
  "3542xc":"#05ff05",
  "354c":"#00b140",
  "355":"#0fff00",
  "355c":"#009639",
  "356":"#09ba00",
  "356c":"#007a33",
  "357":"#167000",
  "357c":"#215732",
  "358":"#baff9e",
  "358c":"#addc91",
  "359":"#a3ff82",
  "359c":"#a1d884",
  "360":"#6bff33",
  "360c":"#6cc24a",
  "361":"#4fff00",
  "361c":"#43b02a",
  "362":"#46e800",
  "362c":"#509e2f",
  "363":"#3ec200",
  "363c":"#4c8c2b",
  "364":"#349400",
  "364c":"#4a7729",
  "365":"#e0ffb5",
  "365c":"#c2e189",
  "366":"#ccff8f",
  "366c":"#b7dd79",
  "367":"#adff69",
  "367c":"#a4d65e",
  "368":"#6eff00",
  "3682x":"#1cfc03",
  "3682xc":"#1cfc03",
  "368c":"#78be20",
  "369":"#61ed00",
  "369c":"#64a70b",
  "370":"#52ba00",
  "370c":"#658d1b",
  "371":"#407000",
  "371c":"#546223",
  "372":"#e6ffab",
  "372c":"#d4eb8e",
  "373":"#d6ff8a",
  "373c":"#cdea80",
  "374":"#c2ff6e",
  "374c":"#c5e86c",
  "375":"#96ff38",
  "3752x":"#38ff00",
  "3752xc":"#38ff00",
  "375c":"#97d700",
  "376":"#74f200",
  "376c":"#84bd00",
  "377":"#6bc200",
  "377c":"#7a9a01",
  "378":"#436600",
  "378c":"#59621d",
  "379":"#e8ff6b",
  "379c":"#e2e868",
  "380":"#deff47",
  "380c":"#dbe442",
  "381":"#ccff17",
  "381c":"#cedc00",
  "382":"#b5ff00",
  "3822x":"#9cfc03",
  "3822xc":"#9cfc03",
  "382c":"#c4d600",
  "383":"#a5cf00",
  "383c":"#a8ad00",
  "384":"#90b000",
  "384c":"#949300",
  "385":"#686b00",
  "385c":"#787121",
  "386":"#f0ff70",
  "386c":"#e9ec6b",
  "387":"#e6ff42",
  "387c":"#e3e935",
  "388":"#dbff36",
  "388c":"#e0e721",
  "389":"#ccff26",
  "389c":"#d0df00",
  "390":"#b7eb00",
  "390c":"#b5bd00",
  "391":"#95ab00",
  "391c":"#9a9500",
  "392":"#798200",
  "392c":"#827a04",
  "393":"#f7ff73",
  "3935":"#fcff52",
  "3935c":"#f3ea5d",
  "393c":"#f0ec74",
  "394":"#f0ff3d",
  "3945":"#f7ff26",
  "3945c":"#f3e500",
  "394c":"#ede939",
  "395":"#ebff26",
  "3955":"#f0ff00",
  "3955c":"#efdf00",
  "395c":"#ece81a",
  "396":"#e3ff0f",
  "3965":"#ebff00",
  "3965c":"#eedc00",
  "396c":"#e1e000",
  "397":"#cce300",
  "3975":"#b5b500",
  "3975c":"#bba600",
  "397c":"#bfb800",
  "398":"#abb800",
  "3985":"#969200",
  "3985c":"#9a8700",
  "398c":"#ada400",
  "399":"#919100",
  "3995":"#5c5900",
  "3995c":"#685c20",
  "399c":"#a09200",
  "400":"#d6d0c9",
  "400c":"#c4bfb6",
  "401":"#c4bbaf",
  "401c":"#afa9a0",
  "402":"#b0a597",
  "402c":"#9d968d",
  "403":"#918779",
  "403c":"#8c857b",
  "404":"#706758",
  "404c":"#776e64",
  "405":"#474030",
  "405c":"#696158",
  "406":"#d6cbc9",
  "406c":"#c4bcb7",
  "407":"#bdaeac",
  "407c":"#b2a8a2",
  "408":"#a89796",
  "408c":"#978c87",
  "409":"#8c7a77",
  "409c":"#857874",
  "410":"#705c59",
  "410c":"#746661",
  "411":"#47342e",
  "411c":"#5e514d",
  "412":"#050402",
  "412c":"#382f2d",
  "413":"#ccccba",
  "413c":"#babbb1",
  "414":"#b3b3a1",
  "414c":"#a8a99e",
  "415":"#969684",
  "415c":"#919388",
  "416":"#80806b",
  "416c":"#7e7f74",
  "417":"#585943",
  "417c":"#65665c",
  "418":"#3e402c",
  "418c":"#51534a",
  "419":"#000000",
  "419c":"#212322",
  "420":"#d9d9d9",
  "420c":"#c7c9c7",
  "421":"#bdbdbd",
  "421c":"#b2b4b2",
  "422":"#ababab",
  "422c":"#9ea2a2",
  "423":"#8f8f8f",
  "423c":"#898d8d",
  "424":"#636363",
  "424c":"#707372",
  "425":"#3b3b3b",
  "425c":"#54585a",
  "426":"#000000",
  "426c":"#25282a",
  "427":"#e3e3e3",
  "427c":"#d0d3d4",
  "428":"#cdd1d1",
  "428c":"#c1c6c8",
  "429":"#a8adad",
  "429c":"#a2aaad",
  "430":"#858c8c",
  "430c":"#7c878e",
  "431":"#525b5c",
  "431c":"#5b6770",
  "432":"#2d393b",
  "432c":"#333f48",
  "433":"#090c0d",
  "4332x":"#0c1518",
  "4332xc":"#0c1518",
  "433c":"#1d252d",
  "434":"#ede6e8",
  "434c":"#d0c4c5",
  "435":"#ded6db",
  "435c":"#c1b2b6",
  "436":"#c2bfbf",
  "436c":"#ab989d",
  "437":"#8a8c8a",
  "437c":"#7b6469",
  "438":"#394500",
  "438c":"#584446",
  "439":"#293300",
  "439c":"#453536",
  "440":"#202700",
  "440c":"#382e2c",
  "441":"#dae8d8",
  "441c":"#bec6c4",
  "442":"#becfbc",
  "442c":"#a2acab",
  "443":"#9db39d",
  "443c":"#919d9d",
  "444":"#7e947e",
  "444c":"#717c7d",
  "445":"#475947",
  "445c":"#505759",
  "446":"#324031",
  "446c":"#3f4444",
  "447":"#272e20",
  "447c":"#373a36",
  "448":"#2d3e00",
  "4485":"#4f3a00",
  "4485c":"#614f25",
  "448c":"#4a412a",
  "449":"#3d5200",
  "4495":"#8a6e07",
  "4495c":"#8c7732",
  "449c":"#524727",
  "450":"#506700",
  "4505":"#a38b24",
  "4505c":"#998542",
  "450c":"#594a25",
  "451":"#abb573",
  "4515":"#c2b061",
  "4515c":"#b3a369",
  "451c":"#9b945f",
  "452":"#c2cf9c",
  "4525":"#d4c581",
  "4525c":"#c5b783",
  "452c":"#b0aa7e",
  "453":"#dbe3bf",
  "4535":"#e3da9f",
  "4535c":"#cfc493",
  "453c":"#bfbb98",
  "454":"#e8edd6",
  "4545":"#f0e9c2",
  "4545c":"#d5cb9f",
  "454c":"#cac7a7",
  "455":"#594a00",
  "455c":"#695b24",
  "456":"#917c00",
  "456c":"#a28e2a",
  "457":"#b89c00",
  "457c":"#b89d18",
  "458":"#e6e645",
  "458c":"#d9c756",
  "459":"#f0ed73",
  "459c":"#decd63",
  "460":"#f5f28f",
  "460c":"#e4d77e",
  "461":"#f7f7a6",
  "461c":"#e9df97",
  "462":"#402600",
  "4625":"#361500",
  "4625c":"#4f2c1d",
  "462c":"#5c462b",
  "463":"#6b3d00",
  "4635":"#8f4a06",
  "4635c":"#946037",
  "463c":"#744f28",
  "464":"#955300",
  "4642x":"#803007",
  "4642xc":"#803007",
  "4645":"#b8743b",
  "4645c":"#ad7c59",
  "464c":"#8b5b29",
  "465":"#ccad6b",
  "4655":"#d19b73",
  "4655c":"#bf9474",
  "465c":"#b9975b",
  "466":"#e0c791",
  "4665":"#e6bc9c",
  "4665c":"#cda788",
  "466c":"#c6aa76",
  "467":"#e8d9a8",
  "4675":"#f0d5bd",
  "4675c":"#dcbfa6",
  "467c":"#d3bc8d",
  "468":"#f0e8c4",
  "4685":"#f5e4d3",
  "4685c":"#e0c6ad",
  "468c":"#ddcba4",
  "469":"#4a1a00",
  "4695":"#420d00",
  "4695c":"#5b3427",
  "469c":"#693f23",
  "470":"#ab4800",
  "4705":"#823126",
  "4705c":"#7c4d3a",
  "470c":"#a45a2a",
  "471":"#d15600",
  "4712x":"#cc1f05",
  "4712xc":"#cc1f05",
  "4715":"#a8625d",
  "4715c":"#956c58",
  "471c":"#b86125",
  "472":"#ffa87a",
  "4725":"#bf827c",
  "4725c":"#ae8a79",
  "472c":"#e59e6d",
  "473":"#ffc4a3",
  "4735":"#d9a9a7",
  "4735c":"#c0a392",
  "473c":"#f0bf9b",
  "474":"#ffd9bd",
  "4745":"#e6bebc",
  "4745c":"#cdb5a7",
  "474c":"#f1c6a7",
  "475":"#ffe3cc",
  "4755":"#f0d8d3",
  "4755c":"#d7c4b7",
  "475c":"#f3cfb3",
  "476":"#381c00",
  "476c":"#4e3629",
  "477":"#4f1800",
  "477c":"#623b2a",
  "478":"#6b1200",
  "478c":"#703f2a",
  "479":"#b08573",
  "479c":"#aa8066",
  "480":"#d9b5b0",
  "480c":"#c6a992",
  "481":"#e8cfc9",
  "481c":"#d3bba8",
  "482":"#f2e0de",
  "482c":"#dbc8b6",
  "483":"#660700",
  "483c":"#653024",
  "484":"#b50900",
  "484c":"#9a3324",
  "485":"#ff0d00",
  "4852x":"#fc000a",
  "4852xc":"#fc000a",
  "485c":"#da291c",
  "486":"#ff8796",
  "486c":"#e8927c",
  "487":"#ffa6b8",
  "487c":"#eaa794",
  "488":"#ffbdcf",
  "488c":"#ecbaa8",
  "489":"#ffd9e3",
  "489c":"#ecc3b2",
  "490":"#471300",
  "490c":"#5d2a2c",
  "491":"#7a1a00",
  "491c":"#7f3035",
  "492":"#942200",
  "492c":"#8f3237",
  "493":"#f283bb",
  "493c":"#dc8699",
  "494":"#ffabde",
  "494c":"#e9a2b2",
  "495":"#ffc2e3",
  "495c":"#f1bdc8",
  "496":"#ffd6e8",
  "496c":"#f2c6cf",
  "497":"#381100",
  "4975":"#330e00",
  "4975c":"#3f2021",
  "497c":"#512f2e",
  "498":"#662500",
  "4985":"#853241",
  "4985c":"#874b52",
  "498c":"#6a3735",
  "499":"#853100",
  "4995":"#a85868",
  "4995c":"#9c6169",
  "499c":"#7a3e3a",
  "500":"#e38db3",
  "5005":"#c47a8f",
  "5005c":"#b07c83",
  "500c":"#c6858f",
  "501":"#f7b5d7",
  "5015":"#e3aac1",
  "5015c":"#cca1a6",
  "501c":"#daa5ad",
  "502":"#fccfe3",
  "5025":"#edc2d1",
  "5025c":"#dbb7bb",
  "502c":"#e5bac1",
  "503":"#ffe3eb",
  "5035":"#f7dfe1",
  "5035c":"#dfc2c3",
  "503c":"#e9c4c7",
  "504":"#320000",
  "504c":"#572932",
  "505":"#600000",
  "505c":"#6f2c3f",
  "506":"#770000",
  "506c":"#84344e",
  "507":"#de82c4",
  "507c":"#d592aa",
  "508":"#f2a3e3",
  "508c":"#e4a9bb",
  "509":"#ffc2ed",
  "509c":"#e8b3c3",
  "510":"#ffd4f0",
  "510c":"#ebbecb",
  "511":"#3d0066",
  "5115":"#2b0041",
  "5115c":"#512a44",
  "511c":"#612c51",
  "512":"#6100ce",
  "5125":"#592482",
  "5125c":"#693c5e",
  "512c":"#833177",
  "513":"#8a1fff",
  "5135":"#8257b8",
  "5135c":"#7e5475",
  "513c":"#93328e",
  "514":"#d980ff",
  "5145":"#b38ce0",
  "5145c":"#9b7793",
  "514c":"#d48bc8",
  "515":"#ed9eff",
  "5155":"#d4b3eb",
  "5155c":"#bfa5b8",
  "515c":"#e2acd7",
  "516":"#f7baff",
  "5165":"#e8cff2",
  "5165c":"#d3c0cd",
  "516c":"#e6bedd",
  "517":"#ffd1ff",
  "5175":"#f2e0f7",
  "5175c":"#d8c8d1",
  "517c":"#ebc6df",
  "518":"#2e005c",
  "5185":"#1c0022",
  "5185c":"#4a3041",
  "518c":"#4b3048",
  "519":"#44009d",
  "5195":"#3d0c4e",
  "5195c":"#66435a",
  "519c":"#59315f",
  "520":"#5c00e0",
  "5205":"#7a5e85",
  "5205c":"#86647a",
  "520c":"#642f6c",
  "521":"#ba87ff",
  "5215":"#b59ec2",
  "5215c":"#af95a6",
  "521c":"#a57fb2",
  "522":"#d4a1ff",
  "5225":"#d4bad9",
  "5225c":"#c6b0bc",
  "522c":"#ba9cc5",
  "523":"#e6bdff",
  "5235":"#e6d4e6",
  "5235c":"#d0bec7",
  "523c":"#c9b1d0",
  "524":"#f0d9ff",
  "5245":"#f0e6ed",
  "5245c":"#dbcdd3",
  "524c":"#d5c2d8",
  "525":"#270085",
  "5255":"#0d0b4d",
  "5255c":"#1e1a34",
  "525c":"#572c5f",
  "526":"#3b00ed",
  "5265":"#20258a",
  "5265c":"#403a60",
  "526c":"#702f8a",
  "527":"#4500ff",
  "5275":"#3848a8",
  "5275c":"#595478",
  "527c":"#8031a7",
  "528":"#9673ff",
  "5285":"#7280c4",
  "5285c":"#8d89a5",
  "528c":"#b580d1",
  "529":"#bd99ff",
  "5295":"#a8b3e6",
  "5295c":"#b3b0c4",
  "529c":"#caa2dd",
  "530":"#d1b0ff",
  "5305":"#c7ceed",
  "5305c":"#c6c4d2",
  "530c":"#d7b9e4",
  "531":"#e6ccff",
  "5315":"#e4e4f2",
  "5315c":"#d8d7df",
  "531c":"#dfc8e7",
  "532":"#00193f",
  "532c":"#1c1f2a",
  "533":"#00227b",
  "533c":"#1f2a44",
  "534":"#002caa",
  "534c":"#1b365d",
  "535":"#94b3ed",
  "535c":"#8e9fbc",
  "536":"#b0c7f2",
  "536c":"#a2b2c8",
  "537":"#c7dbf7",
  "537c":"#bbc7d6",
  "538":"#dee8fa",
  "538c":"#c5cfda",
  "539":"#00274d",
  "5395":"#00223d",
  "5395c":"#081f2c",
  "539c":"#00263a",
  "540":"#003473",
  "5405":"#3a728a",
  "5405c":"#4f758b",
  "540c":"#003057",
  "541":"#00449e",
  "5415":"#5a8a96",
  "5415c":"#5b7f95",
  "541c":"#003c71",
  "542":"#5ec1f7",
  "5425":"#79a6ad",
  "5425c":"#7a99ac",
  "542c":"#7bafd4",
  "543":"#96e3ff",
  "5435":"#b8cdd4",
  "5435c":"#a6bbc8",
  "543c":"#a4c8e1",
  "544":"#b3f0ff",
  "5445":"#ccdcde",
  "5445c":"#b7c9d3",
  "544c":"#bdd6e6",
  "545":"#c7f7ff",
  "5455":"#dae8e8",
  "5455c":"#bfced6",
  "545c":"#c6dae7",
  "546":"#02272b",
  "5463":"#002b24",
  "5463c":"#07272d",
  "5467":"#000d09",
  "5467c":"#18332f",
  "546c":"#072b31",
  "547":"#003440",
  "5473":"#167a58",
  "5473c":"#115e67",
  "5477":"#1d4230",
  "5477c":"#3e5d58",
  "547c":"#00313c",
  "548":"#00465c",
  "5483":"#43b08b",
  "5483c":"#4f868e",
  "5487":"#48705d",
  "5487c":"#5d7975",
  "548c":"#003d4c",
  "549":"#56adba",
  "5493":"#73c9ad",
  "5493c":"#7fa9ae",
  "5497":"#829e90",
  "5497c":"#829995",
  "549c":"#6ba4b8",
  "550":"#7bc1c9",
  "5503":"#9cdbc5",
  "5503c":"#94b7bb",
  "5507":"#a1b5a8",
  "5507c":"#9db0ac",
  "550c":"#8db9ca",
  "551":"#a2d7de",
  "5513":"#c7f2e1",
  "5513c":"#abc7ca",
  "5517":"#bed1c5",
  "5517c":"#b1c0bc",
  "551c":"#a3c7d2",
  "552":"#c5e8e8",
  "5523":"#dcf7eb",
  "5523c":"#b6cfd0",
  "5527":"#d5e3da",
  "5527c":"#bcc9c5",
  "552c":"#b9d3dc",
  "553":"#143319",
  "5535":"#102e14",
  "5535c":"#183028",
  "553c":"#284734",
  "554":"#115422",
  "5545":"#327a3d",
  "5545c":"#43695b",
  "554c":"#205c40",
  "555":"#187031",
  "5555":"#5a9e68",
  "5555c":"#5c7f71",
  "555c":"#28724f",
  "556":"#66ba80",
  "5565":"#84bd8f",
  "5565c":"#7f9c90",
  "556c":"#6fa287",
  "557":"#98d9ad",
  "5575":"#a9d4b2",
  "5575c":"#92aca0",
  "557c":"#85b09a",
  "558":"#bae8ca",
  "5585":"#cae6cc",
  "5585c":"#a7bdb1",
  "558c":"#9abeaa",
  "559":"#cef0d8",
  "5595":"#ddedda",
  "5595c":"#bfcec2",
  "559c":"#adcab8",
  "560":"#0d4018",
  "5605":"#050f07",
  "5605c":"#22372b",
  "560c":"#1d3c34",
  "561":"#127a38",
  "5615":"#2e522b",
  "5615c":"#5e7461",
  "561c":"#00594c",
  "562":"#1ab058",
  "5625":"#5a7d57",
  "5625c":"#708573",
  "562c":"#006f62",
  "563":"#79fcac",
  "5635":"#89a386",
  "5635c":"#94a596",
  "563c":"#6bbbae",
  "564":"#a1ffcc",
  "5645":"#aebfa6",
  "5645c":"#a3b2a4",
  "564c":"#86c8bc",
  "565":"#c4ffde",
  "5655":"#c5d1be",
  "5655c":"#b0bdb0",
  "565c":"#a1d6ca",
  "566":"#dbffe8",
  "5665":"#dae6d5",
  "5665c":"#bac5b9",
  "566c":"#b9dcd2",
  "567":"#0e4d1c",
  "567c":"#173f35",
  "568":"#14a346",
  "568c":"#006c5b",
  "569":"#04d45b",
  "569c":"#00816d",
  "570":"#85ffb5",
  "570c":"#6bcaba",
  "571":"#adffcf",
  "571c":"#98dbce",
  "572":"#c4ffdb",
  "572c":"#a5dfd3",
  "573":"#dbffe8",
  "573c":"#b5e3d8",
  "574":"#314a0e",
  "5743":"#1f2e07",
  "5743c":"#3e4827",
  "5747":"#243600",
  "5747c":"#3d441e",
  "574c":"#4e5b31",
  "575":"#3e7800",
  "5753":"#3f5410",
  "5753c":"#5e6738",
  "5757":"#547306",
  "5757c":"#6d712e",
  "575c":"#67823a",
  "576":"#4f9c00",
  "5763":"#5c6e1d",
  "5763c":"#737b4c",
  "5767":"#849c32",
  "5767c":"#8a8d4a",
  "576c":"#789d4a",
  "577":"#aee67c",
  "5773":"#909e5a",
  "5773c":"#899064",
  "5777":"#a5b85e",
  "5777c":"#a2a569",
  "577c":"#a9c47f",
  "578":"#c0f090",
  "5783":"#afba86",
  "5783c":"#a3aa83",
  "5787":"#cede99",
  "5787c":"#babd8b",
  "578c":"#b7ce95",
  "579":"#cdf7a3",
  "5793":"#c9d1a5",
  "5793c":"#b3b995",
  "5797":"#dce8b0",
  "5797c":"#c6c89b",
  "579c":"#bcd19b",
  "580":"#dcfab9",
  "5803":"#dee3c8",
  "5803c":"#c3c6a8",
  "5807":"#e9f0ce",
  "5807c":"#d0d1ab",
  "580c":"#c4d6a4",
  "581":"#464700",
  "5815":"#363605",
  "5815c":"#555025",
  "581c":"#625d20",
  "582":"#788a00",
  "5825":"#69660e",
  "5825c":"#89813d",
  "582c":"#8e8c13",
  "583":"#a3d400",
  "5835":"#999632",
  "5835c":"#a09958",
  "583c":"#b7bf10",
  "584":"#d3f032",
  "5845":"#b3b15f",
  "5845c":"#afa96e",
  "584c":"#d2d755",
  "585":"#defa55",
  "5855":"#d1d190",
  "5855c":"#c0bb87",
  "585c":"#dbde70",
  "586":"#e8ff78",
  "5865":"#dedea6",
  "5865c":"#cbc793",
  "586c":"#e0e27c",
  "587":"#f2ff99",
  "5875":"#ebebc0",
  "5875c":"#d2ce9e",
  "587c":"#e3e48d",
  "600":"#ffffb5",
  "600c":"#f1eb9c",
  "601":"#ffff99",
  "601c":"#f0e991",
  "602":"#ffff7d",
  "602c":"#f0e87b",
  "603":"#fcfc4e",
  "603c":"#ede04b",
  "604":"#f7f71e",
  "604c":"#eada24",
  "605":"#ede800",
  "605c":"#e1cd00",
  "606":"#e0d700",
  "606c":"#cfb500",
  "607":"#fcfccf",
  "607c":"#ebe49a",
  "608":"#fafaaa",
  "608c":"#e9e186",
  "609":"#f5f584",
  "609c":"#e6de77",
  "610":"#f0f065",
  "610c":"#e1d555",
  "611":"#e3e112",
  "611c":"#d7c826",
  "612":"#ccc800",
  "612c":"#c4b000",
  "613":"#b3ab00",
  "613c":"#b39b00",
  "614":"#f5f5c4",
  "614c":"#dcd59a",
  "615":"#f0edaf",
  "615c":"#d6cf8d",
  "616":"#e8e397",
  "616c":"#d0c883",
  "617":"#d4cf6e",
  "617c":"#c0b561",
  "618":"#b3ad17",
  "618c":"#ac9f3c",
  "619":"#918c00",
  "619c":"#9f912a",
  "620":"#787200",
  "620c":"#8a7b19",
  "621":"#d9fae1",
  "621c":"#d1e0d7",
  "622":"#baf5c6",
  "622c":"#b7cdc2",
  "623":"#9ce6ae",
  "623c":"#9ab9ad",
  "624":"#72cc85",
  "624c":"#789f90",
  "625":"#4bab60",
  "625c":"#507f70",
  "626":"#175e22",
  "626c":"#285c4d",
  "627":"#04290a",
  "627c":"#13322b",
  "628":"#cffff0",
  "628c":"#b8dde1",
  "629":"#a8ffe8",
  "629c":"#9bd3dd",
  "630":"#87ffe3",
  "630c":"#77c5d5",
  "631":"#52fadc",
  "631c":"#3eb1c8",
  "632":"#13f2ce",
  "632c":"#0093b2",
  "633":"#00bfac",
  "633c":"#007396",
  "634":"#00998b",
  "634c":"#005f83",
  "635":"#adffeb",
  "635c":"#a4dbe8",
  "636":"#8cffe8",
  "636c":"#8bd3e6",
  "637":"#73ffe8",
  "637c":"#4ec3e0",
  "638":"#2bffe6",
  "638c":"#00afd7",
  "639":"#00f2e6",
  "639c":"#0095c8",
  "640":"#00c7c7",
  "640c":"#0082ba",
  "641":"#00abb3",
  "641c":"#0067a0",
  "642":"#d2f0fa",
  "642c":"#d1dde6",
  "643":"#b8e4f5",
  "643c":"#c6d6e3",
  "644":"#8bccf0",
  "644c":"#9bb8d3",
  "645":"#64a7e8",
  "645c":"#7da1c4",
  "646":"#4696e3",
  "646c":"#5e8ab4",
  "647":"#0056c4",
  "647c":"#236192",
  "648":"#002d75",
  "648c":"#002e5d",
  "649":"#d9edfc",
  "649c":"#dbe2e9",
  "650":"#bee3fa",
  "650c":"#ced9e5",
  "651":"#95c5f0",
  "651c":"#a7bcd6",
  "652":"#5c97e6",
  "652c":"#7d9bc1",
  "653":"#004ecc",
  "653c":"#326295",
  "654":"#00399e",
  "654c":"#003a70",
  "655":"#002b7a",
  "655c":"#002554",
  "656":"#dbf5ff",
  "656c":"#dde5ed",
  "657":"#c2ebff",
  "657c":"#c8d8eb",
  "658":"#96ccff",
  "658c":"#b1c9e8",
  "659":"#5ca6ff",
  "659c":"#7ba4db",
  "660":"#1a6eff",
  "660c":"#407ec9",
  "661":"#0048e8",
  "661c":"#003594",
  "662":"#003bd1",
  "662c":"#001a70",
  "663":"#edf0ff",
  "663c":"#e5e1e6",
  "664":"#e3e8ff",
  "664c":"#e0dbe3",
  "665":"#c8cffa",
  "665c":"#c6bcd0",
  "666":"#a4a6ed",
  "666c":"#a192b2",
  "667":"#6970db",
  "667c":"#7c6992",
  "668":"#3e40b3",
  "668c":"#614b79",
  "669":"#201e87",
  "669c":"#3f2a56",
  "670":"#ffdeff",
  "670c":"#ead3e2",
  "671":"#fcccff",
  "671c":"#e6bcd8",
  "672":"#f7a8ff",
  "672c":"#dfa0c9",
  "673":"#f082ff",
  "673c":"#d986ba",
  "674":"#e854ff",
  "674c":"#c6579a",
  "675":"#cd00f7",
  "675c":"#ae2573",
  "676":"#bb00c7",
  "676c":"#960051",
  "677":"#fadeff",
  "677c":"#e5cedb",
  "678":"#f7c9ff",
  "678c":"#e3c8d8",
  "679":"#f2baff",
  "679c":"#debed2",
  "680":"#e18efa",
  "680c":"#c996b6",
  "681":"#c15ff5",
  "681c":"#b06c96",
  "682":"#a82fe0",
  "682c":"#994878",
  "683":"#810091",
  "683c":"#7c2855",
  "684":"#facffa",
  "684c":"#e4c6d4",
  "685":"#f7baf7",
  "685c":"#dcb6c9",
  "686":"#f2aaf2",
  "686c":"#d0a1ba",
  "687":"#dc7ee0",
  "687c":"#be84a3",
  "688":"#c459cf",
  "688c":"#a76389",
  "689":"#9d27a8",
  "689c":"#893b67",
  "690":"#690369",
  "690c":"#612141",
  "691":"#fcd7e8",
  "691c":"#e9cdd0",
  "692":"#fac0e1",
  "692c":"#e4bec3",
  "693":"#f0a8d3",
  "693c":"#d7a3ab",
  "694":"#e683ba",
  "694c":"#c48490",
  "695":"#bf508a",
  "695c":"#b46b7a",
  "696":"#991846",
  "696c":"#984856",
  "697":"#7d0925",
  "697c":"#893c47",
  "698":"#ffd6eb",
  "698c":"#f2d4d7",
  "699":"#ffc2e6",
  "699c":"#f4c3cc",
  "700":"#ffa3db",
  "700c":"#f2acb9",
  "701":"#ff78cc",
  "701c":"#e68699",
  "702":"#f24ba0",
  "702c":"#d25b73",
  "703":"#d62463",
  "703c":"#b83a4b",
  "704":"#ba0025",
  "704c":"#9e2a2f",
  "705":"#ffe8f2",
  "705c":"#f5dadf",
  "706":"#ffd4e6",
  "706c":"#f7ced7",
  "707":"#ffb3db",
  "707c":"#f9b5c4",
  "708":"#ff8ac7",
  "708c":"#f890a5",
  "709":"#ff579e",
  "709c":"#ef6079",
  "710":"#ff366b",
  "710c":"#e03e52",
  "711":"#fa0032",
  "711c":"#cb2c30",
  "712":"#ffdbb0",
  "712c":"#fcc89b",
  "713":"#ffcf96",
  "713c":"#fdbe87",
  "714":"#ffb875",
  "714c":"#fdaa63",
  "715":"#ffa14a",
  "715c":"#f68d2e",
  "716":"#ff8717",
  "716c":"#ea7600",
  "717":"#fa7000",
  "717c":"#d45d00",
  "718":"#eb6300",
  "718c":"#be4d00",
  "719":"#ffe6bf",
  "719c":"#edc8a3",
  "720":"#fcd7a7",
  "720c":"#e7b78a",
  "721":"#f7bc77",
  "721c":"#dda46f",
  "722":"#e89538",
  "722c":"#c88242",
  "723":"#d4740b",
  "723c":"#b36924",
  "724":"#a14c00",
  "724c":"#934d11",
  "725":"#823b00",
  "725c":"#7d3f16",
  "726":"#fae6c0",
  "726c":"#e0c09f",
  "727":"#f2cea0",
  "727c":"#d9b48f",
  "728":"#e6b577",
  "728c":"#cda077",
  "729":"#d19052",
  "729c":"#b58150",
  "730":"#b56e2b",
  "730c":"#9e652e",
  "731":"#753700",
  "731c":"#774212",
  "732":"#5c2800",
  "732c":"#623412",
  "7401":"#fff5d1",
  "7401c":"#f5e1a4",
  "7402":"#fff0b3",
  "7402c":"#ecd898",
  "7403":"#ffe680",
  "7403c":"#eed484",
  "7404":"#ffe833",
  "7404c":"#f4da40",
  "7405":"#ffe600",
  "7405c":"#f2cd00",
  "7406":"#ffd100",
  "7406c":"#f1c400",
  "7407":"#e3b122",
  "7407c":"#cba052",
  "7408":"#ffbf0d",
  "7408c":"#f6be00",
  "7409":"#ffb30d",
  "7409c":"#f0b323",
  "7410":"#ffb373",
  "7410c":"#fead77",
  "7411":"#ffa64f",
  "7411c":"#e6a65d",
  "7412":"#ed8a00",
  "7412c":"#d38235",
  "7413":"#f57300",
  "7413c":"#dc8633",
  "7414":"#e37b00",
  "7414c":"#c16c18",
  "7415":"#ffd1d9",
  "7415c":"#e6baa8",
  "7416":"#ff6666",
  "7416c":"#e56a54",
  "7417":"#ff4040",
  "7417c":"#e04e39",
  "7418":"#f24961",
  "7418c":"#cd545b",
  "7419":"#d15473",
  "7419c":"#b04a5a",
  "7420":"#cc2976",
  "7420c":"#9b2242",
  "7421":"#630046",
  "7421c":"#651d32",
  "7422":"#ffe8f2",
  "7422c":"#f4cdd4",
  "7423":"#ff73c7",
  "7423c":"#e06287",
  "7424":"#ff40b3",
  "7424c":"#e24585",
  "7425":"#ed18a6",
  "7425c":"#b52555",
  "7426":"#d10073",
  "7426c":"#a4123f",
  "7427":"#b80040",
  "7427c":"#971b2f",
  "7428":"#73173f",
  "7428c":"#6a2c3e",
  "7429":"#ffd1f7",
  "7429c":"#e2bccb",
  "7430":"#fab0ff",
  "7430c":"#dca9bf",
  "7431":"#f296ed",
  "7431c":"#c9809e",
  "7432":"#e667df",
  "7432c":"#b55c80",
  "7433":"#d936b8",
  "7433c":"#a73a64",
  "7434":"#cc29ad",
  "7434c":"#9b3259",
  "7435":"#a60095",
  "7435c":"#872651",
  "7436":"#f7ebff",
  "7436c":"#eedaea",
  "7437":"#f0ccff",
  "7437c":"#ccaed0",
  "7438":"#d9a6ff",
  "7438c":"#d59ed7",
  "7439":"#cca6ff",
  "7439c":"#b288b9",
  "7440":"#b399ff",
  "7440c":"#a277a6",
  "7441":"#a380ff",
  "7441c":"#9f5cc0",
  "7442":"#804dff",
  "7442c":"#963cbd",
  "7443":"#f0f2ff",
  "7443c":"#dddae8",
  "7444":"#ccd4ff",
  "7444c":"#b6b8dc",
  "7445":"#adc6f7",
  "7445c":"#a7a2c3",
  "7446":"#919eff",
  "7446c":"#8986ca",
  "7447":"#5357cf",
  "7447c":"#5d4777",
  "7448":"#4e4373",
  "7448c":"#4b384c",
  "7449":"#270020",
  "7449c":"#41273b",
  "7450":"#cce6ff",
  "7450c":"#bdc5db",
  "7451":"#99c9ff",
  "7451c":"#89abe3",
  "7452":"#80adff",
  "7452c":"#8094dd",
  "7453":"#80bdff",
  "7453c":"#7ba6de",
  "7454":"#73aee6",
  "7454c":"#5f8fb4",
  "7455":"#3378ff",
  "7455c":"#3a5dae",
  "7456":"#6b9aed",
  "7456c":"#606eb2",
  "7457":"#e0fffa",
  "7457c":"#bbdde6",
  "7458":"#90f0e4",
  "7458c":"#71b2c9",
  "7459":"#5fded1",
  "7459c":"#4298b5",
  "7460":"#00f2f2",
  "7460c":"#0086bf",
  "7461":"#38b8ff",
  "7461c":"#007dba",
  "7462":"#0073e6",
  "7462c":"#00558c",
  "7463":"#003359",
  "7463c":"#002b49",
  "7464":"#bfffe6",
  "7464c":"#a0d1ca",
  "7465":"#80ffbf",
  "7465c":"#40c1ac",
  "7466":"#4dffc4",
  "7466c":"#00b0b9",
  "7467":"#0dffbf",
  "7467c":"#00a3ad",
  "7468":"#00a5b8",
  "7468c":"#007398",
  "7469":"#007a99",
  "7469c":"#005f86",
  "7470":"#1c778c",
  "7470c":"#005a70",
  "7471":"#b8ffdb",
  "7471c":"#7eddd3",
  "7472":"#7affbf",
  "7472c":"#5cb8b2",
  "7473":"#46eb91",
  "7473c":"#279989",
  "7474":"#14c78f",
  "7474c":"#007681",
  "7475":"#59b386",
  "7475c":"#487a7b",
  "7476":"#00663a",
  "7476c":"#0d5257",
  "7477":"#105249",
  "7477c":"#244c5a",
  "7478":"#d1ffdb",
  "7478c":"#9be3bf",
  "7479":"#73ff80",
  "7479c":"#26d07c",
  "7480":"#66ff80",
  "7480c":"#00bf6f",
  "7481":"#66ff73",
  "7481c":"#00b74f",
  "7482":"#33ff40",
  "7482c":"#009f4d",
  "7483":"#117300",
  "7483c":"#275d38",
  "7484":"#008013",
  "7484c":"#00573f",
  "7485":"#f0ffe6",
  "7485c":"#d0debb",
  "7486":"#ccffb3",
  "7486c":"#bce194",
  "7487":"#b3ff8c",
  "7487c":"#8edd65",
  "7488":"#91ff66",
  "7488c":"#78d64b",
  "7489":"#5fed2f",
  "7489c":"#74aa50",
  "7490":"#5ba621",
  "7490c":"#719949",
  "7491":"#689900",
  "7491c":"#79863c",
  "7492":"#d1ed77",
  "7492c":"#bfcc80",
  "7493":"#c5e693",
  "7493c":"#bbc592",
  "7494":"#a3d982",
  "7494c":"#9caf88",
  "7495":"#86b324",
  "7495c":"#8f993e",
  "7496":"#5f9e00",
  "7496c":"#76881d",
  "7497":"#738639",
  "7497c":"#7a7256",
  "7498":"#263300",
  "7498c":"#5b6236",
  "7499":"#fffad9",
  "7499c":"#f1e6b2",
  "7500":"#f7f2d2",
  "7500c":"#dfd1a7",
  "7501":"#f0e6c0",
  "7501c":"#d9c89e",
  "7502":"#e6d395",
  "7502c":"#ceb888",
  "7503":"#bfa87c",
  "7503c":"#a89968",
  "7504":"#997354",
  "7504c":"#94795d",
  "7505":"#735022",
  "7505c":"#816040",
  "7506":"#fff2d9",
  "7506c":"#efdbb2",
  "7507":"#ffe6b3",
  "7507c":"#fcd299",
  "7508":"#f5d093",
  "7508c":"#e1b87f",
  "7509":"#f2c279",
  "7509c":"#d6a461",
  "7510":"#e39f40",
  "7510c":"#c6893f",
  "7511":"#bf6900",
  "7511c":"#b77729",
  "7512":"#ab5c00",
  "7512c":"#a6631b",
  "7513":"#f7cbb2",
  "7513c":"#e1b7a7",
  "7514":"#f2b896",
  "7514c":"#d5a286",
  "7515":"#e09270",
  "7515c":"#c58b68",
  "7516":"#a65000",
  "7516c":"#99552b",
  "7517":"#8f3900",
  "7517c":"#85431e",
  "7518":"#663d2e",
  "7518c":"#6d4f47",
  "7519":"#423500",
  "7519c":"#5e4b3c",
  "7520":"#ffd6cf",
  "7520c":"#eabeb0",
  "7521":"#e6acb8",
  "7521c":"#c09c83",
  "7522":"#d68196",
  "7522c":"#b46a55",
  "7523":"#cc7a85",
  "7523c":"#ab5c57",
  "7524":"#ba544a",
  "7524c":"#a45248",
  "7525":"#b36259",
  "7525c":"#9a6a4f",
  "7526":"#a63a00",
  "7526c":"#8a391b",
  "7527":"#ede8df",
  "7527c":"#d6d2c4",
  "7528":"#e6dfcf",
  "7528c":"#c5b9ac",
  "7529":"#d4cbba",
  "7529c":"#b7a99a",
  "7530":"#ada089",
  "7530c":"#a39382",
  "7531":"#80735d",
  "7531c":"#7a6855",
  "7532":"#594a2d",
  "7532c":"#63513d",
  "7533":"#261e06",
  "7533c":"#473729",
  "7534":"#e6e1d3",
  "7534c":"#d1ccbd",
  "7535":"#ccc6ad",
  "7535c":"#b7b09c",
  "7536":"#ada687",
  "7536c":"#a69f88",
  "7537":"#c6ccb8",
  "7537c":"#a7aca2",
  "7538":"#a2b39b",
  "7538c":"#949a90",
  "7539":"#a0a395",
  "7539c":"#8e9089",
  "7540":"#474747",
  "7540c":"#4b4f54",
  "7541":"#edf2f2",
  "7541c":"#d9e1e2",
  "7542":"#c1d6d0",
  "7542c":"#a4bcc2",
  "7543":"#a6b3b3",
  "7543c":"#98a4ae",
  "7544":"#8a9799",
  "7544c":"#768692",
  "7545":"#495c5e",
  "7545c":"#425563",
  "7546":"#304547",
  "7546c":"#253746",
  "7547":"#0a0f0f",
  "7547c":"#131e29",
  "7548c":"#ffc600",
  "7549c":"#ffb500",
  "7550c":"#d19000",
  "7551c":"#b47e00",
  "7552c":"#73531d",
  "7553c":"#5a4522",
  "7554c":"#4b3d2a",
  "7555c":"#d29f13",
  "7556c":"#b78b20",
  "7557c":"#9f7d23",
  "7558c":"#967126",
  "7559c":"#8f6a2a",
  "7560c":"#7d622e",
  "7561c":"#6c5d34",
  "7562c":"#bd9b60",
  "7563c":"#d69a2d",
  "7564c":"#db8a06",
  "7565c":"#cd7925",
  "7566c":"#ad6433",
  "7567c":"#89532f",
  "7568c":"#775135",
  "7569c":"#d78825",
  "7570c":"#d3832b",
  "7571c":"#c67d30",
  "7572c":"#b67233",
  "7573c":"#a7662b",
  "7574c":"#9e6a38",
  "7575c":"#835d32",
  "7576c":"#db864e",
  "7577c":"#e07e3c",
  "7578c":"#dc6b2f",
  "7579c":"#dc582a",
  "7580c":"#c05131",
  "7581c":"#864a33",
  "7582c":"#674736",
  "7583c":"#c4622d",
  "7584c":"#ba5826",
  "7585c":"#af5c37",
  "7586c":"#9e5330",
  "7587c":"#924c2e",
  "7588c":"#7b4d35",
  "7589c":"#5c4738",
  "7590c":"#d4b59e",
  "7591c":"#c07d59",
  "7592c":"#b15533",
  "7593c":"#9d432c",
  "7594c":"#7c3a2d",
  "7595c":"#6b3d2e",
  "7596c":"#5c3d31",
  "7597c":"#d14124",
  "7598c":"#bd472a",
  "7599c":"#b33d26",
  "7600c":"#8d3f2b",
  "7601c":"#83412c",
  "7602c":"#7b4931",
  "7603c":"#674230",
  "7604c":"#e4d5d3",
  "7605c":"#e1bbb4",
  "7606c":"#d6938a",
  "7607c":"#c26e60",
  "7608c":"#a4493d",
  "7609c":"#823b34",
  "7610c":"#683431",
  "7611c":"#ddbcb0",
  "7612c":"#ca9a8e",
  "7613c":"#bc8a7e",
  "7614c":"#a37f74",
  "7615c":"#866761",
  "7616c":"#6b4c4c",
  "7617c":"#583d3e",
  "7618c":"#c66e4e",
  "7619c":"#c04c36",
  "7620c":"#b7312c",
  "7621c":"#ab2328",
  "7622c":"#93272c",
  "7623c":"#8a2a2b",
  "7624c":"#802f2d",
  "7625c":"#e1523d",
  "7626c":"#c63527",
  "7627c":"#a72b2a",
  "7628c":"#9e2a2b",
  "7629c":"#6d3332",
  "7630c":"#633231",
  "7631c":"#572d2d",
  "7632c":"#d6c9ca",
  "7633c":"#c4a4a7",
  "7634c":"#c16784",
  "7635c":"#c63663",
  "7636c":"#bc204b",
  "7637c":"#912f46",
  "7638c":"#7e2d40",
  "7639c":"#936d73",
  "7640c":"#934054",
  "7641c":"#8e2c48",
  "7642c":"#732e4a",
  "7643c":"#672e45",
  "7644c":"#582d40",
  "7645c":"#502b3a",
  "7646c":"#a56e87",
  "7647c":"#a83d72",
  "7648c":"#991e66",
  "7649c":"#8a1b61",
  "7650c":"#722257",
  "7651c":"#6a2a5b",
  "7652c":"#5e2751",
  "7653c":"#948794",
  "7654c":"#a2789c",
  "7655c":"#a15a95",
  "7656c":"#8e3a80",
  "7657c":"#6e2b62",
  "7658c":"#6a3460",
  "7659c":"#5d3754",
  "7660c":"#9991a4",
  "7661c":"#8d6e97",
  "7662c":"#7a4183",
  "7663c":"#6b3077",
  "7664c":"#653279",
  "7665c":"#5e366e",
  "7666c":"#5c4e63",
  "7667c":"#6e7ca0",
  "7668c":"#686e9f",
  "7669c":"#615e9b",
  "7670c":"#565294",
  "7671c":"#514689",
  "7672c":"#4c4184",
  "7673c":"#535486",
  "7674c":"#878cb4",
  "7675c":"#7c7fab",
  "7676c":"#7566a0",
  "7677c":"#6f5091",
  "7678c":"#68478d",
  "7679c":"#563d82",
  "7680c":"#523178",
  "7681c":"#94a9cb",
  "7682c":"#6787b7",
  "7683c":"#426da9",
  "7684c":"#385e9d",
  "7685c":"#2c5697",
  "7686c":"#1d4f91",
  "7687c":"#1d428a",
  "7688c":"#4698cb",
  "7689c":"#298fc2",
  "7690c":"#0076a8",
  "7691c":"#006298",
  "7692c":"#005587",
  "7693c":"#004976",
  "7694c":"#01426a",
  "7695c":"#7ba7bc",
  "7696c":"#6399ae",
  "7697c":"#4e87a0",
  "7698c":"#41748d",
  "7699c":"#34657f",
  "7700c":"#165c7d",
  "7701c":"#005776",
  "7702c":"#48a9c5",
  "7703c":"#009cbd",
  "7704c":"#0085ad",
  "7705c":"#007096",
  "7706c":"#006a8e",
  "7707c":"#00617f",
  "7708c":"#005670",
  "7709c":"#63b1bc",
  "7710c":"#00a7b5",
  "7711c":"#0097a9",
  "7712c":"#00859b",
  "7713c":"#007d8a",
  "7714c":"#007680",
  "7715c":"#006269",
  "7716c":"#00968f",
  "7717c":"#00857d",
  "7718c":"#007672",
  "7719c":"#006d68",
  "7720c":"#00635b",
  "7721c":"#005e5d",
  "7722c":"#005151",
  "7723c":"#50a684",
  "7724c":"#00966c",
  "7725c":"#008755",
  "7726c":"#007b4b",
  "7727c":"#006f44",
  "7728c":"#006845",
  "7729c":"#005844",
  "7730c":"#4b9560",
  "7731c":"#228848",
  "7732c":"#007a3e",
  "7733c":"#007041",
  "7734c":"#286140",
  "7735c":"#36573b",
  "7736c":"#395542",
  "7737c":"#6ba539",
  "7738c":"#48a23f",
  "7739c":"#319b42",
  "7740c":"#3a913f",
  "7741c":"#44883e",
  "7742c":"#4a773c",
  "7743c":"#44693d",
  "7744c":"#babc16",
  "7745c":"#abad23",
  "7746c":"#999b30",
  "7747c":"#888d30",
  "7748c":"#7c8034",
  "7749c":"#727337",
  "7750c":"#656635",
  "7751c":"#cab64b",
  "7752c":"#cfb023",
  "7753c":"#c1a01e",
  "7754c":"#a08629",
  "7755c":"#897630",
  "7756c":"#736635",
  "7757c":"#675e33",
  "7758c":"#d4c304",
  "7759c":"#c4b200",
  "7760c":"#91852c",
  "7761c":"#747136",
  "7762c":"#5d6439",
  "7763c":"#585c3b",
  "7764c":"#535435",
  "7765c":"#bbb323",
  "7766c":"#b4a91f",
  "7767c":"#aa9d2e",
  "7768c":"#8f7e35",
  "7769c":"#716135",
  "7770c":"#635939",
  "7771c":"#4e4934",
  "801":"#17ffe3",
  "8012x":"#03e0f5",
  "8012xc":"#03e0f5",
  "801c":"#009ace",
  "802":"#6bff05",
  "8022x":"#40ff03",
  "8022xc":"#40ff03",
  "802c":"#44d62c",
  "803":"#fce81f",
  "8032x":"#ffc408",
  "8032xc":"#ffc408",
  "803c":"#ffe900",
  "804":"#ff5914",
  "8042x":"#ff360a",
  "8042xc":"#ff360a",
  "804c":"#ffaa4d",
  "805":"#ff2138",
  "8052x":"#ff0f14",
  "8052xc":"#ff0f14",
  "805c":"#ff7276",
  "806":"#fa0f96",
  "8062x":"#fc087a",
  "8062xc":"#fc087a",
  "806c":"#ff3eb5",
  "807":"#e60aeb",
  "8072x":"#db03f7",
  "8072xc":"#db03f7",
  "807c":"#ea27c2",
  "808":"#26ff7a",
  "8082x":"#0aff47",
  "8082xc":"#0aff47",
  "808c":"#26ff7a",
  "809":"#e8f70f",
  "8092x":"#e6ed08",
  "8092xc":"#e6ed08",
  "809c":"#e8f70f",
  "810":"#ffad0f",
  "8102x":"#ff940d",
  "8102xc":"#ff940d",
  "810c":"#ffad0f",
  "811":"#ff2e14",
  "8112x":"#ff1a0a",
  "8112xc":"#ff1a0a",
  "811c":"#ff2e14",
  "812":"#ff0f73",
  "8122x":"#ff083b",
  "8122xc":"#ff083b",
  "812c":"#ff0f73",
  "813":"#eb0dc9",
  "8132x":"#ed05cc",
  "8132xc":"#ed05cc",
  "813c":"#eb0dc9",
  "814":"#4069ff",
  "8142x":"#3047ff",
  "8142xc":"#3047ff",
  "814c":"#4069ff",
  "871c":"#84754e",
  "872c":"#85714d",
  "873c":"#866d4b",
  "874c":"#8b6f4e",
  "875c":"#87674f",
  "876c":"#8b634b",
  "877c":"#8a8d8f",
  "black":"#050403",
  "black0961c":"#9e978e",
  "black2":"#000000",
  "black22x":"#0d1617",
  "black22xc":"#0d1617",
  "black2c":"#332f21",
  "black3":"#000000",
  "black32x":"#0e1619",
  "black32xc":"#0e1619",
  "black3c":"#212721",
  "black4":"#000000",
  "black42x":"#0e1616",
  "black42xc":"#0e1616",
  "black4c":"#31261d",
  "black5":"#000000",
  "black52x":"#0f1318",
  "black52xc":"#0f1318",
  "black5c":"#3e2b2e",
  "black6":"#000000",
  "black62x":"#0b1416",
  "black62xc":"#0b1416",
  "black6c":"#101820",
  "black7":"#21211e",
  "black72x":"#253230",
  "black72xc":"#253230",
  "black7c":"#3d3935",
  "blackc":"#2d2926",
  "blue072":"#001df2",
  "blue072c":"#10069f",
  "blue0821c":"#74d1ea",
  "bluec":"#0018a8",
  "brightgreenc":"#00b08b",
  "brightorangec":"#ff5e00",
  "brightredc":"#f93822",
  "coolgray1":"#f0f0f0",
  "coolgray10":"#474747",
  "coolgray10c":"#63666a",
  "coolgray11":"#363636",
  "coolgray11c":"#53565a",
  "coolgray1c":"#d9d9d6",
  "coolgray2":"#e3e3e3",
  "coolgray2c":"#d0d0ce",
  "coolgray3":"#d1d1d1",
  "coolgray3c":"#c8c9c7",
  "coolgray4":"#bababa",
  "coolgray4c":"#bbbcbc",
  "coolgray5":"#a8a8a8",
  "coolgray5c":"#b1b3b3",
  "coolgray6":"#9e9e9e",
  "coolgray6c":"#a7a8aa",
  "coolgray7":"#878787",
  "coolgray7c":"#97999b",
  "coolgray8":"#707070",
  "coolgray8c":"#888b8d",
  "coolgray9":"#595959",
  "coolgray9c":"#75787b",
  "coolgrayc":"#97999b",
  "darkbluec":"#00239c",
  "green":"#00ff69",
  "green0921c":"#9de7d7",
  "green2x":"#00fc1c",
  "green2xc":"#00fc1c",
  "greenc":"#00ab84",
  "magenta0521c":"#f1b2dc",
  "mediumbluec":"#0084ca",
  "mediumpurplec":"#4e008e",
  "mediumyellowc":"#ffd900",
  "neutralblackc":"#222223",
  "orange021":"#ff7800",
  "orange021c":"#fe5000",
  "orangec":"#ff5800",
  "pantoneyellow":"#fffc00",
  "pantoneyellowc":"#fffc00",
  "pinkc":"#d62598",
  "processblack":"#1c1b1a",
  "processblackc":"#1c1b1a",
  "processblue":"#00cfe6",
  "processblue2x":"#0096ff",
  "processblue2xc":"#0096ff",
  "processbluec":"#0085ca",
  "processcyanc":"#009ee3",
  "processmagentac":"#e6007e",
  "processyellow":"#ffff00",
  "processyellowc":"#ffff00",
  "purple":"#9e1fff",
  "purple2x":"#b300e8",
  "purple2xc":"#b300e8",
  "purplec":"#bb29bb",
  "red032":"#ff1a24",
  "red032c":"#ef3340",
  "red0331c":"#fcaebb",
  "redc":"#ed2939",
  "reflexblue":"#0043fa",
  "reflexblue2x":"#0303fc",
  "reflexblue2xc":"#0303fc",
  "reflexbluec":"#001489",
  "rhodaminered":"#f71cff",
  "rhodaminered2x":"#f71cff",
  "rhodaminered2xc":"#f71cff",
  "rhodamineredc":"#e10098",
  "rubinered":"#f500d0",
  "rubinered2x":"#f500d0",
  "rubinered2xc":"#f500d0",
  "rubineredc":"#ce0058",
  "strongredc":"#ce0056",
  "violet":"#0500ff",
  "violet0631c":"#bf9bde",
  "violet2x":"#0000fc",
  "violet2xc":"#0000fc",
  "violetc":"#440099",
  "warmgray1":"#f0ebe9",
  "warmgray10":"#736353",
  "warmgray10c":"#796e65",
  "warmgray11":"#615040",
  "warmgray11c":"#6e6259",
  "warmgray1c":"#d7d2cb",
  "warmgray2":"#e8e3dc",
  "warmgray2c":"#cbc4bc",
  "warmgray3":"#d4cbc3",
  "warmgray3c":"#bfb8af",
  "warmgray4":"#c2bab0",
  "warmgray4c":"#b6ada5",
  "warmgray5":"#b5aca3",
  "warmgray5c":"#aca39a",
  "warmgray6":"#b0a59b",
  "warmgray6c":"#a59c94",
  "warmgray7":"#9e9188",
  "warmgray7c":"#968c83",
  "warmgray8":"#91847a",
  "warmgray8c":"#8c8279",
  "warmgray9":"#87786c",
  "warmgray9c":"#83786f",
  "warmgrayc":"#a89a8e",
  "warmred":"#ff401a",
  "warmred2x":"#ff030d",
  "warmred2xc":"#ff030d",
  "warmredc":"#f9423a",
  "yellow012c":"#ffd700",
  "yellow0131c":"#f2f0a1",
  "yellow2x":"#fcd605",
  "yellow2xc":"#fcd605",
  "yellowc":"#fedd00",
};
